import debounce from 'debounce-promise';

import { type OptionData as UserOption } from '@atlaskit/user-picker';
import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';

import { LruCache } from './LruCache';

const FETCH_SUGGESTIONS_DEBOUNCE_MS = 200;

const debouncedFetch = debounce(performHttpFetch<RecommendedUser[]>, FETCH_SUGGESTIONS_DEBOUNCE_MS);

export const aifcUserPickerLruCache: {
	get: (key: string) =>
		| {
				lastAccessed: number;
				value: Promise<RecommendedUser[]>;
		  }
		| undefined;
	set: (key: string, value: Promise<RecommendedUser[]>) => void;
	delete: (key: string) => void;
	clear: () => void;
} = LruCache<Promise<RecommendedUser[]>>(10);

type RecommendedUser = {
	accountId: string;
	accountType?: 'atlassian' | 'app';
	avatarUrl?: string;
	avatarUrls?: {
		'16x16': string;
		'24x24': string;
		'32x32': string;
		'48x48': string;
	};
	displayName?: string;
};

export const fetchSuggestions = async (
	query: string | null,
	projectKey: string,
): Promise<UserOption[]> => {
	const cached = aifcUserPickerLruCache.get(query ?? '');

	let apiCall = cached?.value;

	if (!apiCall) {
		const request = debouncedFetch({
			path: `/rest/api/3/user/recommend?${new URLSearchParams({
				context: 'People',
				project: projectKey,
				query: query ?? '',
			})}`,
			options: {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
			},
		});
		aifcUserPickerLruCache.set(query ?? '', request);
		apiCall = request;
	}

	return apiCall.then((data) =>
		data
			.filter((item) => item.accountType !== 'app' && item.accountId)
			.map((item) => {
				return {
					id: item.accountId,
					accountId: item.accountId,
					name: item.displayName || '',
					avatarUrl: item.avatarUrl || item.avatarUrls?.['32x32'] || '',
				};
			}),
	);
};
